/**
 * Artefakt-Register — die „Pässe" des Sibit-Twins. KANONISCHE QUELLE.
 *
 * Blueprint: xp0.ai/70_Produkte-IP/Patterns/Glass-Box Blueprint - Der Twin trägt den Akt.docx.
 * Ein Artefakt ist ein amtliches Dokument IM Twin (Name + Erkennung + Version).
 * Es ist die EINZIGE Kopplungs-Schnittstelle zwischen Räumen: ein Raum verlangt
 * Artefakte (needs) und stellt eines aus (produces) — niemand referenziert die
 * Internas eines anderen Raums. Der Twin (= der Bürger) trägt seine Mappe; jeder
 * Raum liest autonom „liegt Dokument X vor?".
 *
 * ⚠ MIRROR: in hub/src/lib/pipeline-gates.ts gespiegelt (Sync-Header dort).
 * Bei Änderungen BEIDE Kopien angleichen (Mirror-Pattern wie calc-expense.ts).
 */

export const CURRENT_SCHEMA_VERSION = "1.1.0";
export const LEGACY_SCHEMA_VERSION = "1.0.0";

export type CardKind = "timesheet" | "expense" | "unknown";

/** Minimal-Shape, die Frontend-Card UND Hub-DB-Row erfüllen. */
export interface CardLike {
  kind?: CardKind | null;
  state: string;
  employee_id?: string | null;
  data?: any;
}

/** Twin-schema_version aus der Mappe; ungestempelte Karten = Legacy 1.0.0. */
export function readTwinVersion(data: any): string {
  return (data && data._twin && data._twin._schema_version) || LEGACY_SCHEMA_VERSION;
}

export type ArtifactId =
  | "routed" | "ocr_extract" | "employee_assigned"
  | "calculation" | "reviewed" | "exported";

export interface Artifact {
  id: ArtifactId;
  label: string;
  /** Erkennung im Twin (Mappe). Liefert true, wenn das Dokument vorliegt. */
  detect: (c: CardLike) => boolean;
}

const arr = (x: any) => (Array.isArray(x) ? x : []);
const POST_VALIDATION = new Set(["validated", "calculated", "exported"]);
const POST_CALC = new Set(["calculated", "exported"]);

function hasDays(c: CardLike): boolean {
  const days = c.data?.days;
  return !!days && Object.keys(days).some((k) => /^\d+$/.test(k));
}
function hasExtract(c: CardLike): boolean {
  // kind-abhängig: expense → entries, timesheet → days. POST_CALC beweist es
  // state-monoton sound (calc verlangt Extraktion); die Falsch-Grün-States
  // ocr_complete/pending_review sind NICHT in POST_CALC.
  const dataExtract = (c.kind === "expense" || c.kind === "unknown")
    ? arr(c.data?.entries).length > 0
    : hasDays(c);
  return dataExtract || POST_CALC.has(c.state);
}

/** Das Register aller Twin-„Pässe". */
export const ARTIFACTS: Record<ArtifactId, Artifact> = {
  routed: { id: "routed", label: "Routing-Bestätigung",
    detect: (c) => c.kind === "expense" || c.kind === "timesheet" || c.kind == null },
  ocr_extract: { id: "ocr_extract", label: "Extraktions-Beleg (Posten/Zellen)",
    detect: hasExtract },
  employee_assigned: { id: "employee_assigned", label: "Mitarbeiter-Zuordnung",
    detect: (c) => (!!c.employee_id && POST_VALIDATION.has(c.state)) || POST_CALC.has(c.state) },
  calculation: { id: "calculation", label: "Berechnungs-Beleg (Tagessatz/KV)",
    detect: (c) => !!c.data?.calculation || POST_CALC.has(c.state) },
  reviewed: { id: "reviewed", label: "Review-Freigabe",
    detect: (c) => POST_CALC.has(c.state) },
  exported: { id: "exported", label: "RZL-Export-Beleg",
    detect: (c) => c.state === "exported" },
};

export interface ArtifactState {
  id: ArtifactId;
  label: string;
  present: boolean;
  version: string | null; // Twin-Version, unter der das Dokument ausgestellt ist
  stale: boolean;         // present, aber Version < CURRENT → Lazy-Migration nötig
}

/** Status eines Artefakts in der Mappe des Twins. */
export function detectArtifact(card: CardLike, id: ArtifactId): ArtifactState {
  const a = ARTIFACTS[id];
  let present = false;
  try { present = a.detect(card); } catch { present = false; }
  const version = present ? readTwinVersion(card.data) : null;
  const stale = present && version !== CURRENT_SCHEMA_VERSION;
  return { id, label: a.label, present, version, stale };
}

/** Die ganze Mappe des Twins (alle Pässe + Status). */
export function twinDossier(card: CardLike): ArtifactState[] {
  return (Object.keys(ARTIFACTS) as ArtifactId[]).map((id) => detectArtifact(card, id));
}
